import { POST, DELETE } from '@/shared/api/requests';
import { uploadPDF } from '@/features/pdf-editor/api/index';
import { Layout } from '../types/types';

interface AttachPdfOptions {
  pageNumber?: number;
  cropRegion?: [number, number, number, number] | null;
}

/**
 * Attach a PDF page (and optional crop region) to a layout.
 * @param layoutId    - The layout ID
 * @param fileOrPdfId - Either a File object to upload or an existing PDFFile UUID
 * @param options     - Optional page number (default 1) and crop region [x, y, w, h]
 * @param projectId   - Required only when uploading a new File
 */
export const attachPdfToLayout = async (
  layoutId: string,
  fileOrPdfId: File | string,
  options: AttachPdfOptions = {},
  projectId?: number
): Promise<{ status: string; layout: Layout }> => {
  const { pageNumber = 1, cropRegion = null } = options;

  let pdfFileId: string;

  if (fileOrPdfId instanceof File) {
    if (!projectId) {
      throw new Error('projectId is required when uploading a new PDF file');
    }
    const pdfFile = await uploadPDF(fileOrPdfId, projectId);
    pdfFileId = pdfFile.id;
  } else {
    pdfFileId = fileOrPdfId;
  }

  const response = await POST<{ pdf_file_id: string; page_number: number; pdf_crop_region?: number[] | null }>(
    `/layouts/${layoutId}/attach-pdf/`,
    {
      pdf_file_id: pdfFileId,
      page_number: pageNumber,
      ...(cropRegion !== null ? { pdf_crop_region: cropRegion } : {}),
    }
  );

  return response.data;
};

/**
 * Detach PDF (and crop region) from a layout.
 * @param layoutId - The layout ID
 */
export const detachPdfFromLayout = async (
  layoutId: string
): Promise<{ status: string; layout: Layout }> => {
  const response = await DELETE<void>(`/layouts/${layoutId}/attach-pdf/`);
  return response.data;
};
